import type { Rating } from '@contracts';
import { BatteryLow, CloudDrizzle, Cloud, Sun, Sparkles } from '@ui/shared/icons';

const RATINGS: readonly Rating[] = [1, 2, 3, 4, 5];

/** Mood-tile icon table, reused verbatim from desktop's `MorningCheckInCard`
 *  (today-view.md §1.25 v2.6) -- same taxonomy on both surfaces. */
const MOOD_TILES: ReadonlyArray<{ readonly rating: Rating; readonly label: string; readonly icon: typeof BatteryLow }> = [
  { rating: 1, label: 'Low', icon: BatteryLow },
  { rating: 2, label: 'Off', icon: CloudDrizzle },
  { rating: 3, label: 'Okay', icon: Cloud },
  { rating: 4, label: 'Good', icon: Sun },
  { rating: 5, label: 'Peak', icon: Sparkles },
];

/**
 * Mood/energy capture row (today-view.md §1.25 v2.6 -- phone-native
 * redesign matching desktop's visual quality: icon tiles for mood, a
 * segmented meter bar for energy, both reused from `MorningCheckInCard`'s
 * visual language but re-specced against phone's own width/touch-target
 * budget, not a literal port). Two full-width stacked rows -- Mood, then
 * Energy -- each with its own visible text label directly above its
 * independent 5-option scale; this v2.4 stacked-row architecture is what
 * actually fixed the v2.3 overflow defect and is unchanged by v2.6. No XP/
 * celebration of any kind. Re-tapping the already-selected value clears it
 * (one-tap-undo via the existing revert mechanism, ME-4/AC-P9) rather than
 * needing a second undo affordance.
 */
export function MoodEnergyRow({
  mood,
  energy,
  onSetMood,
  onSetEnergy,
}: {
  readonly mood: Rating | null;
  readonly energy: Rating | null;
  readonly onSetMood: (next: Rating) => void;
  readonly onSetEnergy: (next: Rating) => void;
}) {
  return (
    <div
      style={{
        padding: '0 var(--layout-page-margin-phone)',
        marginTop: 'var(--space-3)',
        marginBottom: 'var(--space-4)',
      }}
    >
      <GroupLabel>Mood</GroupLabel>
      <div role="radiogroup" aria-label="Mood" style={{ display: 'flex', gap: 'var(--space-2)' }}>
        {MOOD_TILES.map((tile) => {
          const selected = mood === tile.rating;
          const Icon = tile.icon;
          return (
            <button
              key={tile.rating}
              type="button"
              role="radio"
              aria-checked={selected}
              aria-label={
                selected
                  ? `Mood: ${tile.label}, selected. Activate again to clear.`
                  : `Mood: ${tile.label}`
              }
              onClick={() => onSetMood(tile.rating)}
              style={{
                width: '52px',
                minHeight: 'var(--touch-target-min)',
                padding: 'var(--space-1) 0',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '2px',
                flex: 'none',
                borderRadius: 'var(--radius-md)',
                border: '1px solid transparent',
                background: selected
                  ? 'color-mix(in srgb, var(--hue-primary-vivid) 14%, var(--surface-raised))'
                  : 'var(--surface-raised)',
              }}
            >
              <Icon size={16} color={selected ? 'var(--hue-primary)' : 'var(--text-muted)'} aria-hidden="true" />
              <span
                aria-hidden="true"
                style={{
                  fontSize: 'var(--type-caption-size)',
                  lineHeight: 'var(--type-caption-line)',
                  fontWeight: selected ? 600 : 400,
                  color: selected ? 'var(--hue-primary)' : 'var(--text-muted)',
                }}
              >
                {tile.label}
              </span>
            </button>
          );
        })}
      </div>

      <div style={{ height: 'var(--space-3)' }} aria-hidden="true" />

      <GroupLabel>Energy</GroupLabel>
      <div role="radiogroup" aria-label="Energy" style={{ display: 'flex', gap: '2px' }}>
        {RATINGS.map((n) => {
          const filled = energy !== null && n <= energy;
          const selected = energy === n;
          return (
            <button
              key={n}
              type="button"
              role="radio"
              aria-checked={selected}
              aria-label={
                selected ? `Energy: ${n}, selected. Activate again to clear.` : `Energy: ${n}`
              }
              onClick={() => onSetEnergy(n)}
              style={{
                flex: 1,
                minHeight: 'var(--touch-target-min)',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  display: 'block',
                  width: '100%',
                  height: 'var(--space-4)',
                  borderRadius:
                    n === 1 ? 'var(--radius-pill) 0 0 var(--radius-pill)' : n === 5 ? '0 var(--radius-pill) var(--radius-pill) 0' : 0,
                  background: filled ? 'var(--hue-primary-vivid)' : 'var(--border-subtle)',
                }}
              />
            </button>
          );
        })}
      </div>
    </div>
  );
}

function GroupLabel({ children }: { readonly children: string }) {
  return (
    <div
      style={{
        fontSize: 'var(--type-caption-size)',
        lineHeight: 'var(--type-caption-line)',
        fontWeight: 500,
        color: 'var(--text-secondary)',
        marginBottom: 'var(--space-1)',
      }}
    >
      {children}
    </div>
  );
}
